// eslint-disable-next-line eag/no-external-date-time-format-functions -- Special format because of Datagrid
import {formatRFC3339} from 'date-fns';
import {Chips, DateTimePicker, Separator, useTranslationContext} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {suffixTestId} from 'shared/web';

import type {EagFilterProps} from '../../types/EagFilterProps';
import {parseDateFilter} from '../../utils/parseDateFilter';
import {useDateRangeFilter} from '../../utils/useDateRangeFilter';
import {QuickFilterChips} from '../QuickFilterChips/QuickFilterChips';
import {RelativeInputsSection} from '../RelativeInputsSection/RelativeInputsSection';

export type DateTimeRangeFilterProps = EagFilterProps & {
  min: string | null;
  max: string | null;
  relativeDates: Array<{value: string; label: string}>;
  relativeInputs: Array<{value: string; label: string}>;
  quickFilters?: Array<{value: string; label: string}>;
};

export function DateTimeRangeFilter(props: DateTimeRangeFilterProps) {
  const t = useTranslationContext();
  const minDate = parseDateFilter(props.min);
  const maxDate = parseDateFilter(props.max);

  const filter = useDateRangeFilter({
    value: props.value,
    onChange: props.onChange,
    dateFormatter: formatRFC3339,
    hasRelativeInputs: true,
  });

  return (
    <VStack>
      <QuickFilterChips
        quickFilters={props.quickFilters}
        value={filter.quickFilterValue}
        defaultValue={null}
        setFilterValue={filter.setFilterValue}
        isDisabled={props.isDisabled}
        data-testid={suffixTestId('date-time-range-filter-quickFilters', props)}
      />
      <Show when={props.relativeDates && props.relativeDates.length > 0}>
        <>
          <Chips
            isDisabled={props.isDisabled}
            value={filter.chipsValue}
            options={props.relativeDates}
            onChange={filter.onChipsChange}
            isMultiple={false}
            data-testid={suffixTestId('date-time-range-filter-relativeDate', props)}
            isDeselectable
          />
          <Separator />
        </>
      </Show>
      <VStack spacing={2}>
        <DateTimePicker
          label={t('page.datagrid.filter.dateTimeLabelFrom')}
          minDate={minDate}
          maxDate={maxDate}
          isDisabled={props.isDisabled}
          value={filter.fromPickerValue}
          onChange={filter.onDateFromChange}
          data-testid={suffixTestId('date-time-range-filter-from', props)}
          isRelativeDatesHidden
        />
        <DateTimePicker
          label={t('page.datagrid.filter.dateTimeLabelTo')}
          minDate={minDate}
          maxDate={maxDate}
          isDisabled={props.isDisabled}
          value={filter.toPickerValue}
          onChange={filter.onDateToChange}
          data-testid={suffixTestId('date-time-range-filter-to', props)}
          isRelativeDatesHidden
        />
      </VStack>
      <RelativeInputsSection
        relativeInputs={props.relativeInputs}
        filter={filter}
        isDisabled={props.isDisabled}
        getInputTestId={(label) =>
          suffixTestId(`date-time-range-filter-relativeInput-${label}`, props)
        }
      />
    </VStack>
  );
}
